<script>
	import Image from '$lib/Components/Layout/Image/Image.svelte';
	import NestedQuote from '$lib/Components/Layout/NestedQuote/NestedQuote.svelte';
	import Section from '$lib/Components/Layout/Section/Section.svelte';
	import ChartEnergyEfficiency from '$lib/PageSections/Charts/ChartEnergyEfficiency/ChartEnergyEfficiency.svelte';
	import CustomerStory from '$lib/PageSections/CustomerStory/CustomerStory.svelte';
	import GetAFreeEstimate from '$lib/PageSections/GetAFreeEstimate/GetAFreeEstimate.svelte';
	import EnergyInAction from '$lib/PageSections/IndowInAction/EnergyInAction.svelte';
	import ProductRecommendation from '$lib/PageSections/Products/ProductRecommendation.svelte';
	import Reviews from '$lib/PageSections/Reviews/Reviews.svelte';
	import TrustedChoice from '$lib/PageSections/TrustedChoice/TrustedChoice.svelte';

	import { ThermalProduct } from '$lib/data/products/products-data.js';
	import { energySavingsCustomerStory as customerStory } from '$routes/(public)/resources/case-studies/customer-stories.js';

	import basketballMetaphorImage from './images/basketballMetaphor.png';
	import usingThermostatImage from './images/usingThermostat.jpg';
</script>

<Section>
	<h2 class="text-left">Why Your Windows Are Wasting Energy</h2>
	<p class="text-left">
		Windows (even double paned) are notoriously bad insulators. Heat escapes
		through your windows during chilly winter months and turns your home into a
		greenhouse during the summer, leading your HVAC to work overtime to keep up.
		That leads to high energy use and increased carbon emissions.
	</p>
	<Image src={usingThermostatImage} loading="eager" />

	<h2 class="text-left">Windows Are Energy <em>Inefficient</em></h2>
	<p class="text-left">
		The human body loses most of its heat from the head, hands, and feet. That's
		why we wear hats, gloves, and heavy socks when we're trying to stay warm in
		the winter. Similarly, drafty windows are the #1 source of temperature loss
		in the home and the leading cause of wasted energy.
	</p>

	<NestedQuote
		quote="Energy-efficient window attachments are important for both new and existing homes. About 30% of a home's heating energy is lost through windows."
		name="US Department of Energy"
		label="energy.gov"
		href="https://www.energy.gov/energysaver/energy-efficient-window-coverings"
		popup
		class="max-w-2xl"
	/>
	<p class="text-left">
		Even a well sealed single pane window can let in a basketball worth of cold
		air every hour (double pane windows aren't much better!), which is why it
		can be so difficult to keep your home a comfortable temperature without
		running the heat all day.
	</p>
	<Image src={basketballMetaphorImage} />

	<h2 class="text-left">Save Money with Indow Thermal Inserts</h2>
	<p class="text-left">
		The US Department of Energy recommends <a
			href="https://www.energy.gov/energysaver/energy-efficient-window-coverings"
			target="_blank">insulating your windows</a
		> with window coverings to reduce energy costs. The biggest value for the amount
		of energy reduction? Interior storm windows - aka window inserts!
	</p>
	<p class="text-left">
		With our patented Snug Fit compression tubing and premium acrylic glazing,
		our <a href="/products/thermal-window-inserts">Thermal Window Inserts</a> reduces
		the amount of cold drafts and heat loss happening through your windows. This reduces
		the load on your HVAC and leads to lower energy use - saving homeowners an average
		of 20% per year on energy expenses!
	</p>
	<p class="text-left">
		But the best part? They blend in with your existing window frames and let
		light flow in freely. In fact, if it wasn't for how much warmer they make
		your home - you'd forget they were there!
	</p>
	<EnergyInAction />
</Section>

<CustomerStory story={customerStory} />

<Section>
	<h2 class="text-left">More Quiet, More Comfort, More Savings</h2>
	<p class="text-left">
		In addition to a substantial reduction in energy costs, Indow Thermal
		Inserts also provide significant noise reduction, keeping your home up to
		50% quieter. It's a win-win-win for your pocket book, the planet, and your
		day-to-day comfort.
	</p>
	<p class="text-left">
		Our air-tight seal not only keeps noise out and your ideal temperature in,
		but it also <a href="/solutions/prevent-window-condensation"
			>reduces condensation</a
		>
		and
		<a href="/solutions/block-pollen-and-pollutants"
			>blocks dust, pollen, and other airborne particles</a
		>. But that doesn't mean you'll have to live in a hermetically sealed home!
		With a gentle tug on the provided D-ring, you can break the seal and remove
		the inserts whenever you want to open your windows and let in some fresh
		air.
	</p>
</Section>

<ProductRecommendation product={ThermalProduct} />

<Section backgroundColor="grey-light">
	<h3 class="text-indow-grey-dark">Indow vs Replacement Windows</h3>
	<p>
		Adding Indow inserts to an existing window can make your windows perform as
		well (or better!) than a replacement window, but at a fraction of the cost
		and without the hassle and disruption of installing new windows.
	</p>
	<ChartEnergyEfficiency />
</Section>

<TrustedChoice />
<Reviews />
<GetAFreeEstimate />
